import * as React from "react";
import { Building2 } from "lucide-react";
import type { Column } from "../../../ui/components/Table";
import * as s from "../../../ui/components/Table/Table.css";
import type { Organization } from "../../lib/api/users";
import { MockedChip } from "../common/MockedChip";

function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function OrgLogo({ logoUrl }: { logoUrl?: string | null }) {
  return (
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: 6,
        border: "1px solid var(--color-border, #E5E7EB)",
        background: "var(--color-muted, #F3F4F6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <Building2 size={16} color="var(--color-fgMuted, #9CA3AF)" />
      )}
    </div>
  );
}

export type OrgListItem = Organization & { id: string };

function getOrgRowStatus(org: OrgListItem): "active" | "disabled" {
  return org.deletedAt ? "disabled" : "active";
}

export const ORG_TABLE_COLUMNS: Column<OrgListItem>[] = [
  {
    id: "logo",
    label: "Logo",
    headerLabelIcon: "user",
    width: 52,
    enableHiding: true,
    render: (row) => <OrgLogo logoUrl={row.logoUrl} />,
  },
  {
    id: "name",
    header: "Name",
    width: 220,
    sortable: true,
    sortKey: "name",
    sort: (a, b) => a.name.localeCompare(b.name),
    render: (row) => (
      <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span className={s.cellTextPrimary}>{row.name}</span>
        {row.isMocked && <MockedChip />}
      </span>
    ),
  },
  {
    id: "status",
    header: "Status",
    width: 110,
    sortable: true,
    sortKey: "status",
    filterKey: "status",
    filterLabel: "Status",
    filterType: "checkbox",
    filterOptions: () => [
      { value: "active", label: "Active" },
      { value: "disabled", label: "Disabled" },
    ],
    filterAccessor: (row) => getOrgRowStatus(row),
    render: (row) => {
      const status = getOrgRowStatus(row);
      return (
        <span
          className={s.cellTextSecondary}
          style={
            status === "disabled"
              ? { color: "#991B1B", fontWeight: 600 }
              : undefined
          }
        >
          {status === "disabled" ? "Disabled" : "Active"}
        </span>
      );
    },
  },
  {
    id: "slug",
    header: "Slug",
    width: 180,
    defaultHidden: true,
    enableHiding: true,
    render: (row) => (
      <span className={s.cellTextSecondary}>{row.slug}</span>
    ),
  },
  {
    id: "description",
    header: "Description",
    minWidth: 200,
    enableHiding: true,
    render: (row) => (
      <span className={s.cellTextSecondary}>
        {row.description || "—"}
      </span>
    ),
  },
  {
    id: "email",
    header: "Email",
    width: 200,
    defaultHidden: true,
    enableHiding: true,
    render: (row) => (
      <span className={s.cellTextSecondary}>{row.email || "—"}</span>
    ),
  },
  {
    id: "phone",
    header: "Phone",
    width: 150,
    defaultHidden: true,
    enableHiding: true,
    render: (row) => (
      <span className={s.cellTextSecondary}>{row.phone || "—"}</span>
    ),
  },
  {
    id: "location",
    header: "Location",
    width: 180,
    enableHiding: true,
    filterKey: "state",
    filterLabel: "State",
    filterType: "checkbox",
    filterOptions: (rows) => {
      const states = Array.from(
        new Set(rows.map((r) => r.state).filter((s): s is string => Boolean(s))),
      ).sort();
      return states.map((s) => ({ value: s, label: s }));
    },
    filterAccessor: (row) => row.state ?? "",
    render: (row) => {
      const parts = [row.city, row.state].filter(Boolean);
      return (
        <span className={s.cellTextSecondary}>
          {parts.length > 0 ? parts.join(", ") : "—"}
        </span>
      );
    },
  },
  {
    id: "createdAt",
    header: "Created",
    width: 140,
    sortable: true,
    sortKey: "created_at",
    defaultHidden: true,
    enableHiding: true,
    render: (row) => (
      <span className={s.cellTextSecondaryTabular}>
        {formatDate(row.createdAt)}
      </span>
    ),
  },
];
